const SCROLL_IDLE_DELAY = 650
const OVERLAY_REMOVE_DELAY = 150
const OVERLAY_CLASS = 'transient-scrollbar-overlay'
const OVERLAY_VISIBLE_CLASS = 'is-visible'
const THUMB_MIN_SIZE = 24
const THUMB_THICKNESS = 6
const TRACK_INSET = 2

let installed = false
const scrollStates = new WeakMap()

function scrollElementFor(eventTarget) {
  if (
    eventTarget === document ||
    eventTarget === window ||
    eventTarget === document.documentElement ||
    eventTarget === document.body
  ) {
    return document.documentElement
  }

  return eventTarget instanceof Element ? eventTarget : null
}

function rootMetrics() {
  const root = document.documentElement
  const body = document.body
  const width = window.innerWidth
  const height = window.innerHeight

  return {
    rect: { top: 0, right: width, bottom: height, left: 0, width, height },
    scrollHeight: Math.max(root.scrollHeight, body?.scrollHeight || 0),
    scrollWidth: Math.max(root.scrollWidth, body?.scrollWidth || 0),
    clientHeight: height,
    clientWidth: width,
    scrollTop: window.scrollY || root.scrollTop,
    scrollLeft: window.scrollX || root.scrollLeft,
  }
}

function elementMetrics(element) {
  if (element === document.documentElement) return rootMetrics()

  const rect = element.getBoundingClientRect()
  const top = Math.max(rect.top, 0)
  const right = Math.min(rect.right, window.innerWidth)
  const bottom = Math.min(rect.bottom, window.innerHeight)
  const left = Math.max(rect.left, 0)

  return {
    rect: {
      top,
      right,
      bottom,
      left,
      width: Math.max(right - left, 0),
      height: Math.max(bottom - top, 0),
    },
    scrollHeight: element.scrollHeight,
    scrollWidth: element.scrollWidth,
    clientHeight: element.clientHeight,
    clientWidth: element.clientWidth,
    scrollTop: element.scrollTop,
    scrollLeft: element.scrollLeft,
  }
}

function createOverlay(orientation) {
  const overlay = document.createElement('div')
  overlay.className = OVERLAY_CLASS
  overlay.dataset.orientation = orientation
  document.body.appendChild(overlay)
  return overlay
}

function stateFor(element) {
  const existingState = scrollStates.get(element)
  if (existingState) return existingState

  const state = {
    vertical: createOverlay('vertical'),
    horizontal: createOverlay('horizontal'),
    idleTimer: null,
    removeTimer: null,
  }

  scrollStates.set(element, state)
  return state
}

function showOverlay(overlay) {
  overlay.classList.add(OVERLAY_VISIBLE_CLASS)
}

function hideOverlay(overlay) {
  overlay.classList.remove(OVERLAY_VISIBLE_CLASS)
}

function positionOverlay(overlay, position) {
  Object.assign(overlay.style, {
    left: `${position.left}px`,
    top: `${position.top}px`,
    width: `${position.width}px`,
    height: `${position.height}px`,
  })
}

function updateVerticalOverlay(state, metrics) {
  const overflow = metrics.scrollHeight - metrics.clientHeight
  if (overflow <= 1 || metrics.rect.height <= 0) {
    hideOverlay(state.vertical)
    return
  }

  const trackHeight = Math.max(metrics.rect.height - TRACK_INSET * 2, 0)
  const thumbHeight = Math.min(
    trackHeight,
    Math.max(THUMB_MIN_SIZE, trackHeight * (metrics.clientHeight / metrics.scrollHeight))
  )
  const scrollRatio = metrics.scrollTop / overflow
  const top = metrics.rect.top + TRACK_INSET + (trackHeight - thumbHeight) * scrollRatio

  positionOverlay(state.vertical, {
    left: metrics.rect.right - THUMB_THICKNESS - TRACK_INSET,
    top,
    width: THUMB_THICKNESS,
    height: thumbHeight,
  })
  showOverlay(state.vertical)
}

function updateHorizontalOverlay(state, metrics) {
  const overflow = metrics.scrollWidth - metrics.clientWidth
  if (overflow <= 1 || metrics.rect.width <= 0) {
    hideOverlay(state.horizontal)
    return
  }

  const trackWidth = Math.max(metrics.rect.width - TRACK_INSET * 2, 0)
  const thumbWidth = Math.min(
    trackWidth,
    Math.max(THUMB_MIN_SIZE, trackWidth * (metrics.clientWidth / metrics.scrollWidth))
  )
  const scrollRatio = metrics.scrollLeft / overflow
  const left = metrics.rect.left + TRACK_INSET + (trackWidth - thumbWidth) * scrollRatio

  positionOverlay(state.horizontal, {
    left,
    top: metrics.rect.bottom - THUMB_THICKNESS - TRACK_INSET,
    width: thumbWidth,
    height: THUMB_THICKNESS,
  })
  showOverlay(state.horizontal)
}

function removeState(element, state) {
  state.vertical.remove()
  state.horizontal.remove()
  scrollStates.delete(element)
}

function markScrolling(element) {
  if (!document.body) return

  const state = stateFor(element)
  const metrics = elementMetrics(element)

  if (state.idleTimer) window.clearTimeout(state.idleTimer)
  if (state.removeTimer) window.clearTimeout(state.removeTimer)

  updateVerticalOverlay(state, metrics)
  updateHorizontalOverlay(state, metrics)

  state.idleTimer = window.setTimeout(() => {
    hideOverlay(state.vertical)
    hideOverlay(state.horizontal)

    state.removeTimer = window.setTimeout(() => {
      removeState(element, state)
    }, OVERLAY_REMOVE_DELAY)
  }, SCROLL_IDLE_DELAY)
}

export function installTransientScrollbars() {
  if (installed || typeof document === 'undefined') return

  installed = true

  document.addEventListener(
    'scroll',
    (event) => {
      const element = scrollElementFor(event.target)
      if (!element) return

      markScrolling(element)
    },
    true
  )
}
